/**
 * PartnerSidebar - Sidebar do painel de parceiros
 *
 * Suporta rotas: /partner, /escritorio, /mfb, /mfa
 * Refatorado para usar BaseSidebar compartilhado
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  BarChart3,
  Megaphone,
  FileText,
  Wallet,
} from 'lucide-react';
import { BaseSidebar, NavigationItem } from './BaseSidebar';
import { useAuth } from '@/contexts/AuthContext.hook';

interface PartnerSidebarProps {
  onSidebarToggle?: (open: boolean) => void;
}

export const PartnerSidebar: React.FC<PartnerSidebarProps> = ({ onSidebarToggle }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { profile } = useAuth();
  const role = profile?.role;

  // Detectar tipo de rota — prioriza prefixo da URL; cai para role quando em rota compartilhada (ex: /products)
  const urlIsMFA = currentPath.startsWith('/mfa');
  const urlIsMFB = currentPath.startsWith('/mfb');
  const urlIsEscritorio = currentPath.startsWith('/escritorio');
  const urlIsPartner = currentPath.startsWith('/partner');
  const urlMatchesRolePrefix = urlIsMFA || urlIsMFB || urlIsEscritorio || urlIsPartner;

  const isMFA = urlIsMFA || (!urlMatchesRolePrefix && role === 'mfa');
  const isMFB = urlIsMFB || (!urlMatchesRolePrefix && role === 'mfb');
  const isEscritorio = urlIsEscritorio;

  // Definir basePath
  const basePath = isMFA ? '/mfa' : isMFB ? '/mfb' : isEscritorio ? '/escritorio' : '/partner';

  // Label personalizado para "Meu escritório" baseado na role
  const carteiraLabel = isMFA
    ? 'Minha regional'
    : isMFB
    ? 'Meu escritório master'
    : 'Meu escritório';

  const navigationItems: NavigationItem[] = [
    {
      label: 'Dashboard',
      href: `${basePath}/dashboard`,
      icon: <LayoutDashboard className="text-sidebar-foreground size-4 shrink-0" />,
    },
    {
      label: carteiraLabel,
      href: `${basePath}/carteira`,
      icon: <Wallet className="text-sidebar-foreground size-4 shrink-0" />,
    },
    ...((isMFB || isMFA) ? [{
      label: 'Detalhes dos Ativos',
      href: `${basePath}/ativos`,
      icon: <BarChart3 className="text-sidebar-foreground size-4 shrink-0" />,
    }] : []),
    {
      label: 'Produtos Gerais',
      href: '/products',
      icon: <Briefcase className="text-sidebar-foreground size-4 shrink-0" />,
    },
    {
      label: 'Comunicados',
      href: `${basePath}/comunicados`,
      icon: <Megaphone className="text-sidebar-foreground size-4 shrink-0" />,
    },
    {
      label: 'Documentos',
      href: `${basePath}/documentos`,
      icon: <FileText className="text-sidebar-foreground size-4 shrink-0" />,
    },
  ];

  return (
    <BaseSidebar
      navigationItems={navigationItems}
      profilePath={`${basePath}/profile`}
      onSidebarToggle={onSidebarToggle}
    />
  );
};

export default PartnerSidebar;
